import {
	Alert,
	AppBar,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	MenuItem,
	Select,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material'
import Transition from 'components/Transition'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import {
	SurveyItemType,
	SurveyMasterType,
	SurveyItemDetailType,
} from 'constants/appType'
import moment from 'moment'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { alertModal } from 'util/util'

type SurveyPopupProps = {
	isOpen: boolean
	onClose: () => void
	data: SurveyMasterType | null
	subData: SurveyItemType[]
	save: (items: SurveyItemType[]) => void
}

const SurveyPopup = ({
	isOpen,
	onClose,
	data,
	subData,
	save,
}: SurveyPopupProps) => {
	// 설문 항목 상태 관리
	const [surveyItems, setSurveyItems] = useState<SurveyItemType[]>([])

	// 유효성 검사 함수
	const validateSurveyItems = (): boolean => {
		// 삭제되지 않은 항목만 검증
		const activeItems = surveyItems.filter((item) => item.crud !== 'D')

		if (activeItems.length === 0) {
			alertModal('설문 항목을 추가해주세요.', 'warning', () => {})
			return false
		}

		for (let i = 0; i < activeItems.length; i++) {
			const item = activeItems[i]
			const itemNumber = i + 1

			// 설문 문항 입력 체크
			if (!item.question || item.question.trim() === '') {
				alertModal(
					`${itemNumber}번 문항의 설문 문항을 입력해주세요.`,
					'warning',
					() => {}
				)
				return false
			}

			// 객관식일 경우 보기 항목 체크
			if (item.isSelectable) {
				if (!item.questionItems || item.questionItems.length === 0) {
					alertModal(
						`${itemNumber}번 문항은 객관식입니다. 최소 1개 이상의 보기를 추가해주세요.`,
						'warning',
						() => {}
					)
					return false
				}

				// 각 보기 항목의 내용 체크
				for (let j = 0; j < item.questionItems.length; j++) {
					const questionItem = item.questionItems[j]
					if (
						!questionItem.questionItem ||
						questionItem.questionItem.trim() === ''
					) {
						alertModal(
							`${itemNumber}번 문항의 ${j + 1}번 보기를 입력해주세요.`,
							'warning',
							() => {}
						)
						return false
					}
				}
			}
		}

		return true
	}

	// 저장 처리
	const handleSave = () => {
		if (validateSurveyItems()) {
			// 모든 항목 전달 (CRUD 상태 포함)
			save(surveyItems)
		}
	}

	// subData가 변경되면 surveyItems 초기화
	useEffect(() => {
		if (subData && subData.length > 0) {
			// 서버 데이터를 프론트엔드 형식으로 변환
			const convertedItems = subData.map((item: any) => ({
				crud: 'R', // 기존 데이터는 Read 상태
				id: item.id,
				surveyId: item.surveyId,
				question: item.question || '',
				isSelectable: item.isSelectable || false,
				isCheckable: item.isCheckable || false,
				seq: item.seq || 0,
				regId: item.regId || '',
				modId: item.modId || '',
				createdAt: item.createdAt || '',
				updatedAt: item.updatedAt || '',
				// questionItems 또는 detailList를 questionItems로 변환
				questionItems: (item.questionItems || item.detailList || []).map(
					(detail: any) => ({
						crud: 'R',
						id: detail.id,
						surveyId: detail.surveyId,
						surveyItemId: detail.surveyItemId,
						seq: detail.seq || 0,
						questionItem: detail.questionItem || '',
						regId: detail.regId || '',
						modId: detail.modId || '',
						createdAt: detail.createdAt || '',
						updatedAt: detail.updatedAt || '',
					})
				),
			}))
			console.log('Converted items:', convertedItems)
			setSurveyItems(convertedItems)
		} else {
			setSurveyItems([])
		}
	}, [subData])

	// 설문 항목 추가
	const handleAddSurveyItem = () => {
		// 신규 항목은 소숫점을 포함한 임시 ID 생성 (자바에서 신규 구분용)
		const tempId = Date.now() + Math.random()
		const newItem: SurveyItemType = {
			crud: 'C',
			id: tempId,
			surveyId: data?.id || 0,
			question: '',
			isSelectable: true,
			isCheckable: false,
			seq: surveyItems.length + 1,
			regId: '',
			modId: '',
			createdAt: '',
			updatedAt: '',
			questionItems: [],
		}
		setSurveyItems([...surveyItems, newItem])
	}

	// 설문 항목 삭제 (마지막 항목)
	const handleRemoveSurveyItem = () => {
		if (surveyItems.length > 0) {
			setSurveyItems(surveyItems.slice(0, -1))
		}
	}

	// 설문 항목 개별 삭제
	const handleRemoveSurveyItemByIndex = (index: number) => {
		const item = surveyItems[index]
		// 기존 데이터는 D(Delete) 상태로 변경, 신규는 배열에서 제거
		if (item.crud === 'C') {
			// 신규 항목은 배열에서 제거
			setSurveyItems(surveyItems.filter((_, idx) => idx !== index))
		} else {
			// 기존 항목은 crud를 'D'로 변경하고, 하위 보기 항목도 모두 'D' 처리
			const updatedItems = [...surveyItems]
			updatedItems[index] = {
				...item,
				crud: 'D',
				questionItems: item.questionItems.map((questionItem) => ({
					...questionItem,
					crud: questionItem.crud === 'C' ? 'C' : 'D', // 신규는 그대로, 기존은 D로 변경
				})),
			}
			setSurveyItems(updatedItems)
		}
	}

	// 설문 항목 업데이트
	const handleUpdateSurveyItem = (
		index: number,
		field: keyof SurveyItemType,
		value: any
	) => {
		const updatedItems = [...surveyItems]
		updatedItems[index] = {
			...updatedItems[index],
			[field]: value,
			crud: updatedItems[index].crud === 'C' ? 'C' : 'U',
		}
		setSurveyItems(updatedItems)
	}

	// 설문 보기 항목 추가
	const handleAddQuestionItem = (surveyItemIndex: number) => {
		const updatedItems = [...surveyItems]
		const surveyItem = updatedItems[surveyItemIndex]

		const newQuestionItem: SurveyItemDetailType = {
			crud: 'C',
			id: Date.now() + Math.random(), // 임시 ID (소숫점)
			surveyId: data?.id || 0,
			surveyItemId: surveyItem.id, // 부모의 id 값을 그대로 참조
			seq: surveyItem.questionItems.length + 1,
			questionItem: '',
			regId: '',
			modId: '',
			createdAt: '',
			updatedAt: '',
		}

		updatedItems[surveyItemIndex] = {
			...surveyItem,
			questionItems: [...surveyItem.questionItems, newQuestionItem],
			crud: surveyItem.crud === 'C' ? 'C' : 'U',
		}
		setSurveyItems(updatedItems)
	}

	// 설문 보기 항목 삭제
	const handleRemoveQuestionItem = (
		surveyItemIndex: number,
		questionItemIndex: number
	) => {
		const updatedItems = [...surveyItems]
		const surveyItem = updatedItems[surveyItemIndex]
		const questionItem = surveyItem.questionItems[questionItemIndex]

		// 신규 항목(C)은 배열에서 제거, 기존 항목(R)은 crud를 'D'로 변경
		if (questionItem.crud === 'C') {
			// 신규 항목은 배열에서 제거
			updatedItems[surveyItemIndex] = {
				...surveyItem,
				questionItems: surveyItem.questionItems.filter(
					(_, idx) => idx !== questionItemIndex
				),
				crud: surveyItem.crud === 'C' ? 'C' : 'U',
			}
		} else {
			// 기존 항목은 crud를 'D'로 변경
			const updatedQuestionItems = [...surveyItem.questionItems]
			updatedQuestionItems[questionItemIndex] = {
				...questionItem,
				crud: 'D',
			}
			updatedItems[surveyItemIndex] = {
				...surveyItem,
				questionItems: updatedQuestionItems,
				crud: surveyItem.crud === 'C' ? 'C' : 'U',
			}
		}
		setSurveyItems(updatedItems)
	}

	// 설문 보기 항목 업데이트
	const handleUpdateQuestionItem = (
		surveyItemIndex: number,
		questionItemIndex: number,
		field: keyof SurveyItemDetailType,
		value: any
	) => {
		const updatedItems = [...surveyItems]
		const surveyItem = updatedItems[surveyItemIndex]
		const updatedQuestionItems = [...surveyItem.questionItems]

		updatedQuestionItems[questionItemIndex] = {
			...updatedQuestionItems[questionItemIndex],
			[field]: value,
			crud: updatedQuestionItems[questionItemIndex].crud === 'C' ? 'C' : 'U',
		}

		updatedItems[surveyItemIndex] = {
			...surveyItem,
			questionItems: updatedQuestionItems,
			crud: surveyItem.crud === 'C' ? 'C' : 'U',
		}
		setSurveyItems(updatedItems)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullScreen
			slots={{
				transition: Transition,
			}}
		>
			<AppBar sx={{ position: 'sticky' }}>
				<Toolbar>
					<Typography
						sx={{ flex: 1 }}
						variant="h6"
						component="div"
					>
						{data?.title}
					</Typography>
					<IconButton
						edge="end"
						color="inherit"
						onClick={onClose}
						aria-label="close"
					>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			<DialogContent>
				<Alert
					icon={<CheckIcon fontSize="inherit" />}
					severity="info"
					sx={{ mb: 2 }}
				>
					<strong>설문 제목</strong> : {data?.title} <br />
					<strong>설문 기간</strong> :{' '}
					{moment(data?.startDate).format('YYYY-MM-DD HH:mm')} ~{' '}
					{moment(data?.endDate).format('YYYY-MM-DD HH:mm')} <br />
					<strong>설문 문항 수</strong> : {surveyItems.length}개
				</Alert>
				<Divider sx={{ mb: 2 }} />
				<Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end' }}>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleAddSurveyItem}
					>
						항목 추가
					</Button>
					<Button
						variant="contained"
						color="error"
						startIcon={<RemoveIcon />}
						onClick={handleRemoveSurveyItem}
						disabled={surveyItems.length === 0}
					>
						항목 삭제
					</Button>
				</Box>
				<Divider sx={{ mb: 2 }} />
				{/* 설문 항목 리스트 */}
				{surveyItems
					.filter((item) => item.crud !== 'D')
					.map((item, surveyIndex) => (
						<Box
							key={item.id}
							sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
								<Typography
									variant="h6"
									sx={{ minWidth: 30 }}
								>
									{surveyIndex + 1}.
								</Typography>
								<Box
									sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}
								>
									<TextField
										label="설문 문항 입력"
										size="small"
										fullWidth
										value={item.question}
										onChange={(e) =>
											handleUpdateSurveyItem(surveyIndex, 'question', e.target.value)
										}
									/>
									<TextField
										label="문항 번호"
										size="small"
										type="number"
										value={item.seq}
										onChange={(e) =>
											handleUpdateSurveyItem(surveyIndex, 'seq', Number(e.target.value))
										}
										sx={{ width: 120 }}
									/>
									<FormControl
										size="small"
										sx={{ width: 150 }}
									>
										<InputLabel>문항 유형</InputLabel>
										<Select
											label="문항 유형"
											value={
												!item.isSelectable
													? 'subjective'
													: item.isCheckable
													? 'multiple'
													: 'objective'
											}
											onChange={(e) => {
												const value = e.target.value
												const updatedItems = [...surveyItems]
												if (value === 'subjective') {
													updatedItems[surveyIndex] = {
														...updatedItems[surveyIndex],
														isSelectable: false,
														isCheckable: false,
														crud: updatedItems[surveyIndex].crud === 'C' ? 'C' : 'U',
													}
												} else if (value === 'objective') {
													updatedItems[surveyIndex] = {
														...updatedItems[surveyIndex],
														isSelectable: true,
														isCheckable: false,
														crud: updatedItems[surveyIndex].crud === 'C' ? 'C' : 'U',
													}
												} else if (value === 'multiple') {
													updatedItems[surveyIndex] = {
														...updatedItems[surveyIndex],
														isSelectable: true,
														isCheckable: true,
														crud: updatedItems[surveyIndex].crud === 'C' ? 'C' : 'U',
													}
												}
												setSurveyItems(updatedItems)
											}}
										>
											<MenuItem value="subjective">주관식</MenuItem>
											<MenuItem value="objective">객관식</MenuItem>
											<MenuItem value="multiple">다중선택</MenuItem>
										</Select>
									</FormControl>
								</Box>
								<IconButton
									color="error"
									size="small"
									onClick={() => handleRemoveSurveyItemByIndex(surveyIndex)}
									title="항목 삭제"
								>
									<RemoveIcon />
								</IconButton>
							</Box>{' '}
							{/* 보기 항목 리스트 */}
							{item.isSelectable && (
								<>
									<Box sx={{ display: 'flex', gap: 2, mb: 2, ml: 5 }}>
										<Button
											variant="outlined"
											size="small"
											startIcon={<AddIcon />}
											onClick={() => handleAddQuestionItem(surveyIndex)}
										>
											보기 추가 {item.questionItems.length}
										</Button>
									</Box>
									<List sx={{ ml: 5 }}>
										{item.questionItems
											.filter((q) => q.crud !== 'D')
											.map((questionItem, questionIndex) => (
												<ListItem
													key={questionItem.id}
													sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 1 }}
												>
													<Typography
														variant="body1"
														sx={{ minWidth: 30 }}
													>
														{questionIndex + 1}.
													</Typography>
													<TextField
														label="보기 입력"
														size="small"
														fullWidth
														value={questionItem.questionItem}
														onChange={(e) =>
															handleUpdateQuestionItem(
																surveyIndex,
																questionIndex,
																'questionItem',
																e.target.value
															)
														}
													/>
													<TextField
														label="순서"
														size="small"
														type="number"
														value={questionItem.seq}
														onChange={(e) =>
															handleUpdateQuestionItem(
																surveyIndex,
																questionIndex,
																'seq',
																Number(e.target.value)
															)
														}
														sx={{ width: 100 }}
													/>
													<IconButton
														color="error"
														size="small"
														onClick={() =>
															handleRemoveQuestionItem(surveyIndex, questionIndex)
														}
													>
														<RemoveIcon />
													</IconButton>
												</ListItem>
											))}
									</List>
								</>
							)}
						</Box>
					))}
			</DialogContent>
			<Divider />
			<DialogActions>
				<Button
					variant="contained"
					onClick={handleSave}
					color="primary"
					size="small"
					startIcon={<SaveAsTwoToneIcon />}
				>
					저장
				</Button>
				<Button
					variant="contained"
					onClick={onClose}
					color="secondary"
					size="small"
					startIcon={<PhonelinkEraseIcon />}
				>
					취소
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default SurveyPopup
